import { useEffect, useId, useState } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { contactService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import './ProjectRequestModal.css';

const SERVICES = [
  'Sistemas sob medida',
  'Websites',
  'Bots Discord',
  'Design',
  'Infraestrutura',
  'Outro',
];

const BUDGETS = [
  'Até R$ 2.000',
  'R$ 2.000 – R$ 5.000',
  'R$ 5.000 – R$ 15.000',
  'R$ 15.000 – R$ 45.000',
  'Acima de R$ 45.000',
  'A definir',
];

const TIMELINES = [
  'Urgente (até 2 semanas)',
  '1 mês',
  '2–3 meses',
  'Flexível',
  'A definir',
];

const ACTIVE_STATUSES = new Set([
  'pending',
  'in_review',
  'awaiting_client',
  'quoted',
  'accepted',
  'in_progress',
]);

const INITIAL = {
  name: '',
  email: '',
  company: '',
  discord: '',
  phone: '',
  services: [],
  budget: 'A definir',
  timeline: 'A definir',
  description: '',
  references: '',
  source: '',
};

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Informe seu nome.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Informe um e-mail válido.';
  }
  if (!form.services.length) errors.services = 'Selecione ao menos um serviço.';
  if (form.description.trim().length < 40) {
    errors.description = 'Descreva o projeto com pelo menos 40 caracteres.';
  }
  return errors;
}

export default function ProjectRequestModal({ open, onClose }) {
  const titleId = useId();
  const { isAuthenticated, user } = useAuth();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);
  const [checkingActive, setCheckingActive] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL);
      setErrors({});
      setStatus(null);
      setSubmitting(false);
      setActiveRequest(null);
      setCheckingActive(false);
      return undefined;
    }

    setForm((current) => ({
      ...current,
      name: user?.profile?.displayName || user?.username || current.name,
      email: user?.email || current.email,
    }));

    if (!isAuthenticated) return undefined;

    let cancelled = false;
    setCheckingActive(true);
    contactService.listMyRequests()
      .then((response) => {
        if (cancelled) return;
        const active = (response?.data?.requests || []).find((item) => (
          ACTIVE_STATUSES.has(item.status)
        ));
        setActiveRequest(active || null);
      })
      .catch(() => {
        if (!cancelled) setActiveRequest(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingActive(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isAuthenticated, user]);

  if (!open) return null;

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const toggleService = (service) => {
    setForm((current) => {
      const exists = current.services.includes(service);
      return {
        ...current,
        services: exists
          ? current.services.filter((item) => item !== service)
          : [...current.services, service],
      };
    });
    setErrors((current) => {
      if (!current.services) return current;
      const next = { ...current };
      delete next.services;
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (activeRequest) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setStatus(null);
    try {
      const result = await contactService.submitProjectRequest({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        discord: form.discord.trim(),
        phone: form.phone.trim(),
        description: form.description.trim(),
        references: form.references.trim(),
        source: form.source.trim(),
      });
      setStatus({
        type: 'success',
        message: result.message || 'Solicitação enviada com sucesso.',
        code: result.data?.request?.code || '',
        linked: Boolean(result.data?.request?.userId),
      });
      setForm(INITIAL);
      setActiveRequest(result.data?.request || null);
    } catch (error) {
      if (error.code === 'ACTIVE_REQUEST_EXISTS' && error.meta?.code) {
        setActiveRequest({
          code: error.meta.code,
          status: error.meta.status,
          statusLabel: error.meta.statusLabel,
          id: error.meta.requestId,
        });
      }
      setStatus({
        type: 'error',
        message: error.message || 'Não foi possível enviar a solicitação.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-request-overlay" role="presentation" onClick={onClose}>
      <div
        className="project-request-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="project-request-header">
          <div>
            <p className="project-request-kicker">Vih&apos;s Studio</p>
            <h2 id={titleId}>Solicitar projeto</h2>
            <p>
              Conte o essencial para entendermos escopo, prazo e direção. Enviamos para
              contato@vihs.com.br. Limite: 1 pedido ativo por vez.
            </p>
          </div>
          <button
            type="button"
            className="project-request-close"
            onClick={onClose}
            aria-label="Fechar"
            disabled={submitting}
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>

        {status?.type === 'success' ? (
          <div className="project-request-success">
            <span className="project-request-success-icon" aria-hidden="true">
              <FaCheck />
            </span>
            <h3>Pedido enviado</h3>
            <p>{status.message}</p>
            {status.code ? (
              <p className="project-request-code">
                Código do pedido: <strong>{status.code}</strong>
              </p>
            ) : null}
            {status.linked ? (
              <p className="project-request-hint">
                Acompanhe o status em <a href="/conta#account-pedidos">Conta → Pedidos</a>.
              </p>
            ) : (
              <p className="project-request-hint">
                Entre ou crie uma conta com o mesmo e-mail para acompanhar o status do pedido.
              </p>
            )}
            <button type="button" className="project-request-submit" onClick={onClose}>
              Fechar
            </button>
          </div>
        ) : checkingActive ? (
          <div className="project-request-success">
            <p>Verificando pedidos em andamento...</p>
          </div>
        ) : activeRequest ? (
          <div className="project-request-success">
            <h3>Pedido em andamento</h3>
            <p>
              Você já tem o pedido <strong>{activeRequest.code}</strong>
              {activeRequest.statusLabel ? ` (${activeRequest.statusLabel})` : ''}.
              Aguarde a conclusão ou o encerramento dele para abrir outro.
            </p>
            <p className="project-request-hint">
              Acompanhe em <a href="/conta#account-pedidos">Conta → Pedidos</a>.
            </p>
            <button type="button" className="project-request-submit" onClick={onClose}>
              Entendi
            </button>
          </div>
        ) : (
          <form className="project-request-form" onSubmit={handleSubmit} noValidate>
            <div className="project-request-grid">
              <label className={errors.name ? 'has-error' : ''}>
                <span>Nome completo *</span>
                <input
                  name="name"
                  autoComplete="name"
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  placeholder="Como devemos te chamar"
                />
                {errors.name && <em>{errors.name}</em>}
              </label>

              <label className={errors.email ? 'has-error' : ''}>
                <span>E-mail *</span>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  placeholder="seu@email.com"
                />
                {errors.email && <em>{errors.email}</em>}
              </label>

              <label>
                <span>Empresa / nome do projeto</span>
                <input
                  name="company"
                  value={form.company}
                  onChange={(event) => updateField('company', event.target.value)}
                  placeholder="Opcional"
                />
              </label>

              <label>
                <span>Discord</span>
                <input
                  name="discord"
                  value={form.discord}
                  onChange={(event) => updateField('discord', event.target.value)}
                  placeholder="usuario ou ID"
                />
              </label>

              <label>
                <span>Telefone / WhatsApp</span>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder="Opcional"
                />
              </label>

              <label>
                <span>Como nos conheceu</span>
                <input
                  name="source"
                  value={form.source}
                  onChange={(event) => updateField('source', event.target.value)}
                  placeholder="Instagram, indicação, Discord..."
                />
              </label>
            </div>

            <fieldset className={errors.services ? 'has-error' : ''}>
              <legend>Tipo de serviço *</legend>
              <div className="project-request-chips">
                {SERVICES.map((service) => {
                  const active = form.services.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      className={`project-request-chip${active ? ' is-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => toggleService(service)}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>
              {errors.services && <em>{errors.services}</em>}
            </fieldset>

            <div className="project-request-grid is-two">
              <label>
                <span>Orçamento estimado</span>
                <select
                  name="budget"
                  value={form.budget}
                  onChange={(event) => updateField('budget', event.target.value)}
                >
                  {BUDGETS.map((budget) => (
                    <option key={budget} value={budget}>{budget}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Prazo desejado</span>
                <select
                  name="timeline"
                  value={form.timeline}
                  onChange={(event) => updateField('timeline', event.target.value)}
                >
                  {TIMELINES.map((timeline) => (
                    <option key={timeline} value={timeline}>{timeline}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className={errors.description ? 'has-error' : ''}>
              <span>Descrição do projeto *</span>
              <textarea
                name="description"
                rows={5}
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
                placeholder="Objetivo, público, funcionalidades, referências de estilo, restrições..."
              />
              {errors.description && <em>{errors.description}</em>}
            </label>

            <label>
              <span>Referências / links</span>
              <textarea
                name="references"
                rows={3}
                value={form.references}
                onChange={(event) => updateField('references', event.target.value)}
                placeholder="Sites, Discord, Figma, Instagram ou materiais úteis"
              />
            </label>

            {status?.type === 'error' && (
              <p className="project-request-alert" role="alert">{status.message}</p>
            )}

            <footer className="project-request-footer">
              <p>Campos com * são obrigatórios. Resposta em horário comercial.</p>
              <button type="submit" className="project-request-submit" disabled={submitting}>
                {submitting ? 'Enviando...' : 'Enviar solicitação'}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}
